import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';

// Public page opened from a WhatsApp rating URL button.
// Route: /feedback/:score/:token — auto-records the score on load.

const API = process.env.REACT_APP_BACKEND_URL;

const BRAND = {
  cream: '#F5F2EA',
  charcoal: '#1A1A1A',
  slate: '#4A4A4A',
  gold: '#C5A059',
  goldSoft: 'rgba(197, 160, 89, 0.25)',
  emerald: '#059669',
};
const fontHead = { fontFamily: "'Cormorant Garamond', 'Georgia', serif", fontWeight: 300, letterSpacing: '-0.01em', color: BRAND.charcoal };
const fontBody = { fontFamily: "'Manrope', 'Inter', system-ui, sans-serif", color: BRAND.charcoal };
const overlineStyle = { fontFamily: "'Manrope', system-ui, sans-serif", fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.2em', color: BRAND.gold };

const FeedbackRatePage = () => {
  const { score, token } = useParams();
  const [state, setState] = useState({ loading: true, err: null, data: null });
  const fired = useRef(false);

  useEffect(() => {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@300;400&family=Manrope:wght@400;500;600;700&display=swap';
    document.head.appendChild(link);
    document.body.style.backgroundColor = BRAND.cream;
    return () => { document.body.style.backgroundColor = ''; };
  }, []);

  useEffect(() => {
    if (fired.current) return;
    fired.current = true;
    (async () => {
      try {
        const res = await axios.post(`${API}/api/public/feedback-rate`, {
          token,
          score: parseInt(score, 10),
        });
        setState({ loading: false, err: null, data: res.data });
      } catch (e) {
        setState({ loading: false, err: e.response?.data?.detail || 'Could not record your feedback', data: null });
      }
    })();
  }, [token, score]);

  const wrap = (children) => (
    <div style={{ minHeight: '100vh', background: BRAND.cream, ...fontBody, padding: '96px 24px' }}>
      <div style={{ maxWidth: 640, margin: '0 auto' }} data-testid="feedback-rate-page">
        <div style={overlineStyle}>Eden Skin Clinic · Feedback</div>
        {children}
        <div style={{ marginTop: 48, paddingTop: 24, borderTop: `1px solid ${BRAND.goldSoft}` }}>
          <p style={{ ...fontBody, color: BRAND.slate, fontSize: 13 }}>
            Need help? Call us at{' '}
            <a href="tel:+917710117777" style={{ color: BRAND.emerald, textDecoration: 'none', fontWeight: 600 }}>+91 77101 17777</a>
          </p>
        </div>
      </div>
    </div>
  );

  if (state.loading) {
    return wrap(<h1 style={{ ...fontHead, fontSize: 42, margin: '16px 0' }}>Recording your feedback…</h1>);
  }

  if (state.err) {
    return wrap(
      <>
        <h1 style={{ ...fontHead, fontSize: 42, margin: '16px 0' }} data-testid="feedback-rate-error">Link unavailable</h1>
        <p style={{ color: BRAND.slate, fontSize: 16, lineHeight: 1.7 }}>{state.err}</p>
      </>
    );
  }

  const d = state.data || {};
  const name = d.patient_first_name ? `, ${d.patient_first_name}` : '';

  if (d.already_rated) {
    return wrap(
      <>
        <h1 style={{ ...fontHead, fontSize: 42, margin: '16px 0' }} data-testid="feedback-rate-already">Already recorded</h1>
        <p style={{ color: BRAND.slate, fontSize: 16, lineHeight: 1.7 }}>
          Thank you{name} — we've already recorded your rating{d.score ? ` of ${d.score}` : ''}. No further action is needed.
        </p>
      </>
    );
  }

  if (d.outcome === 'low_feedback') {
    return wrap(
      <>
        <h1 style={{ ...fontHead, fontSize: 42, margin: '16px 0' }} data-testid="feedback-rate-low">Rating recorded</h1>
        <p style={{ color: BRAND.slate, fontSize: 16, lineHeight: 1.7 }}>
          Thank you{name} — your rating of 4 or less has been recorded.
          We appreciate your honest feedback.
        </p>
      </>
    );
  }

  return wrap(
    <>
      <h1 style={{ ...fontHead, fontSize: 42, margin: '16px 0' }} data-testid="feedback-rate-thanks">Rating recorded</h1>
      <p style={{ color: BRAND.slate, fontSize: 16, lineHeight: 1.7 }}>
        Thank you{name} — your 5-star rating has been recorded.
        We truly appreciate you taking the time.
      </p>
    </>
  );
};

export default FeedbackRatePage;
